import React from 'react';
import { motion } from 'framer-motion';
import { PlayIcon } from 'lucide-react';
import type { Device } from '../data/devices';
type DeviceFrameProps = {
  device: Device;
  /** rendered height of the whole device photo, in px */
  size: number;
  videoId: string;
  scanlines: boolean;
  playing: boolean;
  startAt: number;
  onPlay: () => void;
};
export function DeviceFrame({
  device,
  size,
  videoId,
  scanlines,
  playing,
  startAt,
  onPlay
}: DeviceFrameProps) {
  const photoHeight = size;
  const photoWidth = size * device.imageAspect;
  const rect = device.screenRect;
  const screenWidth = rect.width / 100 * photoWidth;
  const screenHeight = rect.height / 100 * photoHeight;

  // Render the video at the device's TRUE resolution, then stretch it to fill
  // the screen cut-out in the photo — so you really are watching 84×48 pixels.
  const scaleX = screenWidth / device.width;
  const scaleY = screenHeight / device.height;
  const pixel = Math.max(1, Math.min(scaleX, scaleY));
  const params = new URLSearchParams({
    start: String(startAt),
    autoplay: playing ? '1' : '0',
    playsinline: '1',
    rel: '0',
    modestbranding: '1'
  });
  const src = `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
  return <motion.div key={device.id} initial={{
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    type: 'spring',
    stiffness: 240,
    damping: 26
  }} className="flex flex-col items-center gap-5" data-id="element-16" data-mp="j0nnmqtfld6i">
      <div className="relative select-none" style={{
      width: photoWidth,
      height: photoHeight
    }} data-id="element-17" data-mp="3iieww8dfqzru">
        <img src={device.image} alt={`${device.name}, photographed straight on with its screen blank`} className="h-full w-full object-contain" draggable={false} data-id="element-18" data-mp="4xk2z5uo8qi" />

        <div className="absolute overflow-hidden bg-black" style={{
        left: `${rect.left}%`,
        top: `${rect.top}%`,
        width: `${rect.width}%`,
        height: `${rect.height}%`
      }} data-id="element-19" data-mp="33ro3wx03xun5">
          <div style={{
          width: device.width,
          height: device.height,
          transform: `scale(${scaleX}, ${scaleY})`,
          transformOrigin: 'top left',
          filter: device.filter,
          imageRendering: 'pixelated'
        }} data-id="element-20" data-mp="17pkpvshhkcy0">
            <iframe key={`${device.id}-${playing ? 'on' : 'off'}`} title={`${device.name} screen`} src={src} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen style={{
            width: device.width,
            height: device.height,
            border: 0,
            display: 'block'
          }} data-id="element-21" data-mp="jsf4ehn0xi3u" />
          </div>

          {scanlines && <div aria-hidden="true" className="pointer-events-none absolute inset-0 mix-blend-multiply" style={{
          backgroundImage: `repeating-linear-gradient(0deg, rgba(0,0,0,0.3) 0 1px, transparent 1px ${pixel}px), repeating-linear-gradient(90deg, rgba(0,0,0,0.3) 0 1px, transparent 1px ${pixel}px)`
        }} data-id="element-22" data-mp="2958r6r9eejrg" />}

          <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
          boxShadow: 'inset 0 0 12px rgba(0,0,0,0.6)'
        }} data-id="element-23" data-mp="3fx7fid6xbwyb" />

          {!playing && <button type="button" onClick={onPlay} aria-label={`Play the video on the ${device.name}`} className="absolute inset-0 flex items-center justify-center bg-black/60 transition-colors hover:bg-black/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-24" data-mp="361e79608mpmc">
              <span className="flex items-center justify-center rounded-full bg-white/90 text-neutral-900" style={{
            width: Math.max(14, Math.min(screenWidth, screenHeight) * 0.5),
            height: Math.max(14, Math.min(screenWidth, screenHeight) * 0.5)
          }} data-id="element-25" data-mp="v6yvzl5kzj9j">
                <PlayIcon style={{
              width: Math.max(7, Math.min(screenWidth, screenHeight) * 0.24),
              height: Math.max(7, Math.min(screenWidth, screenHeight) * 0.24)
            }} fill="currentColor" data-id="element-26" data-mp="3f34xbmsf4hti" />
              </span>
            </button>}
        </div>

        {playing && <div aria-hidden="true" className="pointer-events-none absolute" style={{
        left: `${rect.left}%`,
        top: `${rect.top}%`,
        width: `${rect.width}%`,
        height: `${rect.height}%`,
        boxShadow: `0 0 ${Math.round(photoHeight * 0.06)}px ${Math.round(photoHeight * 0.02)}px ${device.glow}`
      }} data-id="element-27" data-mp="2wcdr2u3q97jc" />}
      </div>

      <p className="max-w-md text-center font-mono text-xs text-neutral-500" data-id="element-28" data-mp="22qtghevnvfci">
        {device.name} · {device.width}×{device.height}px · {device.blurb}
      </p>
    </motion.div>;
}